import { marketLocationMasterDataMachines } from '@e2m-customer-portal/core';
import {
  Device,
  Machine,
  useMarketLocation,
  useSnackbar,
  useTokenInfo,
  useUpdateDeviceMutation,
} from '@e2m-customer-portal/hooks';
import { isFluctuating, UserRole } from '@e2m-customer-portal/shared';
import { Container, ErrorComponent, MasterDataItem, PageTitle, SubmittableTextField } from '@e2m-customer-portal/ui';
import * as R from 'ramda';
import { ReactElement, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';
import { makeStyles } from 'tss-react/mui-compat';
import { getDisplayName } from '../../../utils';
import { MasterDataDeviceProductLimits } from './MasterDataDeviceProductLimits';

const useStyles = makeStyles()((theme) => ({
  container: {
    margin: theme.spacing(0, 3),
    '&:first-of-type': {
      marginTop: theme.spacing(3),
    },
  },
}));

const devicesFor = R.pipe<[readonly Machine[]], readonly Device[], readonly Device[]>(
  R.chain(R.prop('devices')),
  R.uniqBy(R.prop('id')),
);

const belongsTo = (device: Device) => (machine: Machine) => R.find(R.propEq('id', device.id))(machine.devices);

interface MasterDataDevicesProps {
  marketLocationId: string;
}

export function MasterDataDevices({
  marketLocationId,
}: Readonly<MasterDataDevicesProps>): ReactElement<MasterDataDevicesProps> {
  const { classes } = useStyles();
  const [marketLocation, error] = useMarketLocation(marketLocationId);
  const [t] = useTranslation();
  const { showError } = useSnackbar();
  const { roles } = useTokenInfo();
  const [update] = useUpdateDeviceMutation();

  if (!marketLocation || error) {
    return <ErrorComponent error={error} />;
  }

  const onSubmit =
    (device: Device) =>
    (value: string): void => {
      const updatedExternalName = value.trim();
      update(marketLocation.id, device.id, { externalName: updatedExternalName }).catch(() =>
        showError(t('SHARED_ERROR_UNKNOWN')),
      );
    };

  const machinesFor = (device: Device): Machine[] => marketLocation.machines.filter(belongsTo(device));

  const userCanEditDeviceName = roles.has(UserRole.PlantOperator) || roles.has(UserRole.VPPOperator);
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [hash]);
  return (
    <>
      <PageTitle title={t('MASTER_DATA_HEADER')} />

      {devicesFor(marketLocation.machines).map((device) => (
        <Container
          key={device.id}
          id={device.id}
          title={getDisplayName(device)}
          maxWidth="md"
          className={classes.container}
          collapsible
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-10">
            {userCanEditDeviceName && (
              <SubmittableTextField
                label={t('MASTER_DATA_GRID_EXTERNAL_NAME')}
                placeholder={getDisplayName(device)}
                value={device.externalName ?? ''}
                onSubmit={onSubmit(device)}
                inputProps={{ maxLength: 64 }}
                color={undefined}
                type={undefined}
              />
            )}
            {!userCanEditDeviceName && !!device.externalName?.length && (
              <MasterDataItem header={t('MASTER_DATA_GRID_EXTERNAL_NAME')} body={device.externalName} />
            )}
            <div className="flex flex-col gap-y-5">
              <MasterDataItem header={t('MASTER_DATA_GRID_ALIAS_NAME')} body={device.name} />
              {roles.has(UserRole.VPPOperator) && (
                <MasterDataItem header={t('MASTER_DATA_GRID_DEVICE_ID')}>
                  <Link target="_blank" to={`https://anlagen.energy2market.de/${device.id}`}>
                    {device.id}
                  </Link>
                </MasterDataItem>
              )}
            </div>
            <div className="flex flex-col gap-y-5">
              {!isFluctuating(marketLocation.technologies) && (
                <MasterDataItem header={t('MASTER_DATA_GRID_PARENT')}>
                  <ol className="list-none m-0 p-0">
                    {machinesFor(device).map((machine) => (
                      <li key={machine.id}>
                        <Link to={`${marketLocationMasterDataMachines.getLink(marketLocation.id)}#${machine.id}`}>
                          {t('MASTER_DATA_GRID_MACHINE_LINK', {
                            name: machine.name,
                            capacity: machine.installedPower,
                            unit: 'MW',
                          })}
                        </Link>
                      </li>
                    ))}
                  </ol>
                </MasterDataItem>
              )}
              {roles.has(UserRole.VPPOperator) && (
                <MasterDataItem header={t('MASTER_DATA_GRID_DEVICE_SERIAL_NUMBER')}>
                  <Link target="_blank" to={`https://atlas.energy2market.de/machines/${device.internalId}/masterdata`}>
                    {device.internalId}
                  </Link>
                </MasterDataItem>
              )}
            </div>
            <MasterDataItem
              header={t('MASTER_DATA_GRID_RELEASES_AND_PRODUCT_SERVICES')}
              className="col-span-1 sm:col-span-2 md:col-span-3"
            >
              <MasterDataDeviceProductLimits
                marketLocation={marketLocation}
                deviceInternalId={device.internalId}
                products={device.products}
              />
            </MasterDataItem>
          </div>
        </Container>
      ))}
    </>
  );
}
